/**
 * ScorecardScrollSync - Alpine.js Data Component
 * Manages synchronized scrolling between header, left column, and data content
 * 
 * All functionality is namespaced under 'scrollSync' to avoid key collisions
 * with parent components.
 */
export default () => ({
    // Namespace all scroll sync functionality
    scrollSync: {
        leftScrollListener: null,
        rightScrollListener: null,
        headerScrollListener: null,
        currentInitId: 0,
        
        /**
         * Sync vertical scroll between left content and right scroll
         * Sync horizontal scroll between header and right scroll
         */
        init($refs, mode = 'view', attempt = 1, initId = null, $el = null) {
            if (attempt === 1) {
                this.currentInitId = (this.currentInitId || 0) + 1;
                initId = this.currentInitId;
            }
            if (initId !== this.currentInitId) return;
            let leftScroll, rightScroll, headerScroll;
            const findEl = (refName) => {
                if ($refs[refName]) return $refs[refName];
                if ($el) {
                    return $el.querySelector(`[data-scroll-sync="${refName}"]`);
                }
                return null;
            };

            if (mode === 'customize') {
                leftScroll = findEl('leftContentCustomize');
                rightScroll = findEl('rightScrollCustomize');
                headerScroll = findEl('headerScrollCustomize');
                
                if (!leftScroll || !rightScroll || !headerScroll) {
                    if (attempt <= 20) {
                        setTimeout(() => this.init($refs, mode, attempt + 1, initId, $el), 100);
                        return;
                    }
                    console.warn('ScorecardScrollSync: Missing required refs for mode:', mode, {
                        leftScroll: !!leftScroll,
                        rightScroll: !!rightScroll,
                        headerScroll: !!headerScroll
                    });
                    return;
                }
            } else {
                // In View Mode we have Virtual Scroll, headers are sticky inside the main container, so no sync is needed.
                return;
            }

            // Right scroll controls both vertical (left) and horizontal (header) sync
            this.rightScrollListener = (e) => {
                if (leftScroll) {
                    leftScroll.scrollTop = rightScroll.scrollTop;
                }
                if (headerScroll) {
                    headerScroll.scrollLeft = rightScroll.scrollLeft;
                }
            };
            rightScroll.addEventListener('scroll', this.rightScrollListener);

            // Left scroll controls vertical sync back to right
            if (leftScroll) {
                this.leftScrollListener = (e) => {
                    if (rightScroll) {
                        rightScroll.scrollTop = leftScroll.scrollTop;
                    }
                };
                leftScroll.addEventListener('scroll', this.leftScrollListener);
            }

            // Header scroll controls horizontal sync to right
            this.headerScrollListener = (e) => {
                if (rightScroll) {
                    rightScroll.scrollLeft = headerScroll.scrollLeft;
                }
            };
            headerScroll.addEventListener('scroll', this.headerScrollListener);
        },
        
        /**
         * Clean up event listeners
         */
        cleanup($refs) {
            // Increment initId to invalidate any pending retries
            this.currentInitId = (this.currentInitId || 0) + 1;

            // Clean up listeners
            if (this.rightScrollListener) {
                // We can't easily remove listeners from elements we found via querySelector
                // unless we stored references to them.
                // But since we're likely destroying the component or changing modes,
                // the elements might be gone anyway.
                // However, to be safe, we should probably store the elements we attached to.
            }
            
            // Reset listeners
            this.rightScrollListener = null;
            this.leftScrollListener = null;
            this.headerScrollListener = null;
        }
    },
    
    // Convenience methods for template usage
    initScrollSyncFeature(mode = 'view') {
        // Only proceed if mode is customize
        if (mode !== 'customize') return;

        // Setup watcher if not already set
        if (!this._scrollSyncWatcher && typeof this.$watch === 'function') {
            this._scrollSyncWatcher = this.$watch('displaySections.length', (value) => {
                window.dispatchEvent(new CustomEvent('debug-log', { detail: { name: 'ScrollSync: displaySections watcher' } }));
                if (value > 0) {
                    this.$nextTick(() => {
                        const currentMode = this.showCustomizePanel ? 'customize' : 'view';
                        if (currentMode === 'customize') {
                            this.scrollSync.init(this.$refs, currentMode, 1, null, this.$el);
                        }
                    });
                } else {
                    this.scrollSync.cleanup(this.$refs);
                }
            });
        }

        this.$nextTick(() => {
            // If displaySections exists and is empty, skip init
            if (this.displaySections && this.displaySections.length === 0) {
                return;
            }
            this.scrollSync.init(this.$refs, mode, 1, null, this.$el);
        });
    },
    
    destroyScrollSyncFeature() {
        this.scrollSync.cleanup(this.$refs);
    }
});
